import React, { useEffect, useMemo, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ChevronDown,
  ChevronUp,
  ChevronsDown,
  ChevronsUp,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import {
  FONT_OPTIONS,
  FORMAT_OPTIONS,
  SUMMARY_OPTIONS,
  emptyCustomization,
  type ColumnCustomization,
  type UserCustomizationSettings,
} from '../../types/UserCustomization';
import { buildCellStyleForValue } from '../../utils/applyCustomization';
import { applyFormat } from '../../utils/customizationFormatters';
import { t } from '../../i18n/t';
import { LopButton } from '../LopButton';
import './LopGridCustomizationDialog.css';

// Per-user grid customization dialog. Opens from the right-click context menu
// ("התאמה אישית"). Master-detail layout in the "עמודות" tab: list of columns
// on the right (RTL — visually first), a vertical strip of reorder arrows in
// the middle, and per-column settings on the left.
//
// The dialog owns DRAFT state — changes apply only on Save. Cancel discards.
// Reset = full DELETE on the server + close dialog (parent reloads with no
// customization → defaults).
//
// sampleRowData: the row the user right-clicked on. The selected column's
// settings panel renders a live preview using sampleRowData[selectedField].
//
// initialSelectedField: when the dialog opens from a right-click on a specific
// cell, we land on the columns tab with that column already focused. The
// user's eyes were on that column — start there.

export interface LopGridCustomizationDialogProps {
  resourceCode: string;
  baseColumnDefs: ColDef<any>[];
  current: UserCustomizationSettings | null;
  sampleRowData?: Record<string, unknown> | null;
  initialSelectedField?: string | null;
  onSave: (next: UserCustomizationSettings) => Promise<void>;
  onReset: () => Promise<void>;
  onClose: () => void;
}

interface ColumnMeta {
  field: string;
  headerName: string;
  isNumeric: boolean;
}

function extractColumnMeta(defs: ColDef<any>[]): ColumnMeta[] {
  return defs
    .filter((c): c is ColDef<any> & { field: string } => Boolean(c.field))
    .map((c) => ({
      field: c.field,
      headerName: c.headerName ?? c.field,
      isNumeric: c.cellDataType === 'number',
    }));
}

function initDraft(
  current: UserCustomizationSettings | null,
  baseColumns: ColumnMeta[],
): UserCustomizationSettings {
  const base = current
    ? (JSON.parse(JSON.stringify(current)) as UserCustomizationSettings)
    : emptyCustomization();

  if (!base.grid.columnOrder || base.grid.columnOrder.length === 0) {
    base.grid.columnOrder = baseColumns.map((c) => c.field);
  } else {
    const known = new Set(baseColumns.map((c) => c.field));
    base.grid.columnOrder = base.grid.columnOrder.filter((f) => known.has(f));
    for (const meta of baseColumns) {
      if (!base.grid.columnOrder.includes(meta.field)) {
        base.grid.columnOrder.push(meta.field);
      }
    }
  }

  if (!base.columns) base.columns = {};
  return base;
}

type Tab = 'general' | 'columns';

export function LopGridCustomizationDialog(props: LopGridCustomizationDialogProps) {
  const {
    baseColumnDefs,
    current,
    sampleRowData,
    initialSelectedField,
    onSave,
    onReset,
    onClose,
  } = props;

  const baseColumns = useMemo(() => extractColumnMeta(baseColumnDefs), [baseColumnDefs]);
  const [draft, setDraft] = useState<UserCustomizationSettings>(() =>
    initDraft(current, baseColumns),
  );
  const [tab, setTab] = useState<Tab>(initialSelectedField ? 'columns' : 'general');
  const [selectedField, setSelectedField] = useState<string | null>(
    initialSelectedField ?? baseColumns[0]?.field ?? null,
  );
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const orderedFields = draft.grid.columnOrder ?? baseColumns.map((c) => c.field);
  const metaByField = useMemo(() => {
    const m = new Map<string, ColumnMeta>();
    baseColumns.forEach((c) => m.set(c.field, c));
    return m;
  }, [baseColumns]);

  const filteredFields = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orderedFields;
    return orderedFields.filter((field) => {
      const meta = metaByField.get(field);
      if (!meta) return false;
      return (
        meta.headerName.toLowerCase().includes(q) ||
        meta.field.toLowerCase().includes(q)
      );
    });
  }, [orderedFields, search, metaByField]);

  const selectedMeta = selectedField ? metaByField.get(selectedField) ?? null : null;
  const selectedCustomization: ColumnCustomization =
    (selectedField && draft.columns[selectedField]) || {};
  const sampleValue = selectedField ? sampleRowData?.[selectedField] : undefined;

  function patchGrid(patch: Partial<typeof draft.grid>) {
    setDraft((d) => ({ ...d, grid: { ...d.grid, ...patch } }));
  }
  function patchColumn(field: string, patch: Partial<ColumnCustomization>) {
    setDraft((d) => {
      const existing = d.columns[field] ?? {};
      const merged = { ...existing, ...patch };
      return { ...d, columns: { ...d.columns, [field]: merged } };
    });
  }

  function moveSelected(direction: 'top' | 'up' | 'down' | 'bottom') {
    if (!selectedField) return;
    setDraft((d) => {
      const order = [...(d.grid.columnOrder ?? baseColumns.map((c) => c.field))];
      const i = order.indexOf(selectedField);
      if (i < 0) return d;
      let j = i;
      if (direction === 'top') j = 0;
      else if (direction === 'bottom') j = order.length - 1;
      else if (direction === 'up') j = i - 1;
      else if (direction === 'down') j = i + 1;
      if (j < 0 || j >= order.length || j === i) return d;
      const [moved] = order.splice(i, 1);
      order.splice(j, 0, moved);
      return { ...d, grid: { ...d.grid, columnOrder: order } };
    });
  }

  const selectedIndex = selectedField ? orderedFields.indexOf(selectedField) : -1;
  const canMoveUp = selectedIndex > 0;
  const canMoveDown = selectedIndex >= 0 && selectedIndex < orderedFields.length - 1;

  async function handleSave() {
    setSaving(true);
    setErrorMsg(null);
    try {
      await onSave(draft);
      onClose();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!window.confirm(t('customization.confirmReset'))) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await onReset();
      onClose();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="lop-grid-customization__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lop-grid-customization-title"
    >
      <div className="lop-grid-customization__dialog">
        <header className="lop-grid-customization__header">
          <h2 id="lop-grid-customization-title">{t('customization.dialogTitle')}</h2>
          <button
            type="button"
            className="lop-grid-customization__close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </header>

        <nav className="lop-grid-customization__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'general'}
            className={`lop-grid-customization__tab ${tab === 'general' ? 'is-active' : ''}`}
            onClick={() => setTab('general')}
          >
            {t('customization.tabGeneral')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'columns'}
            className={`lop-grid-customization__tab ${tab === 'columns' ? 'is-active' : ''}`}
            onClick={() => setTab('columns')}
          >
            {t('customization.tabColumns')}
          </button>
        </nav>

        <div className="lop-grid-customization__body">
          {tab === 'general' && (
            <div className="lop-grid-customization__general">
              <label className="lop-grid-customization__row">
                <input
                  type="checkbox"
                  checked={Boolean(draft.grid.zebraStriping)}
                  onChange={(e) => patchGrid({ zebraStriping: e.target.checked })}
                />
                <span>{t('customization.general.zebra')}</span>
              </label>
              <label className="lop-grid-customization__row">
                <span>{t('customization.general.autoRefreshSeconds')}</span>
                <input
                  type="number"
                  min={0}
                  step={5}
                  value={draft.grid.autoRefreshSeconds ?? 0}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    patchGrid({ autoRefreshSeconds: n > 0 ? n : null });
                  }}
                  className="lop-grid-customization__num"
                />
                <span className="lop-grid-customization__hint">
                  {t('customization.general.autoRefreshHint')}
                </span>
              </label>
            </div>
          )}

          {tab === 'columns' && (
            <div className="lop-grid-customization__columns">
              {/* Column list — RTL "right" pane (visually first) */}
              <div className="lop-grid-customization__col-list">
                <div className="lop-grid-customization__col-search">
                  <Search size={14} className="lop-grid-customization__col-search-icon" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('customization.columns.searchPlaceholder')}
                    aria-label={t('customization.columns.searchPlaceholder')}
                  />
                </div>
                <ul>
                  {filteredFields.map((field) => {
                    const meta = metaByField.get(field);
                    if (!meta) return null;
                    const cust = draft.columns[field] ?? {};
                    const isSelected = field === selectedField;
                    const isHidden = Boolean(cust.hidden);
                    // Visible = NOT hidden. Checkbox ticked → column shown.
                    // Unticking strikes through the name (decoration-cue).
                    const visible = !isHidden;
                    return (
                      <li
                        key={field}
                        className={`lop-grid-customization__col-item ${
                          isSelected ? 'is-selected' : ''
                        } ${isHidden ? 'is-hidden' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="lop-grid-customization__col-vis"
                          checked={visible}
                          onChange={(e) =>
                            patchColumn(field, { hidden: !e.target.checked })
                          }
                          aria-label={t('customization.column.hidden')}
                          title={t('customization.column.hidden')}
                        />
                        <button
                          type="button"
                          className="lop-grid-customization__col-name"
                          onClick={() => setSelectedField(field)}
                        >
                          <span className="lop-grid-customization__col-name-text">
                            {meta.headerName}
                          </span>
                          <span className="lop-grid-customization__col-field">
                            {meta.field}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Central arrows — operate on the selected column */}
              <div className="lop-grid-customization__arrows" role="toolbar">
                <button
                  type="button"
                  disabled={!canMoveUp}
                  onClick={() => moveSelected('top')}
                  aria-label={t('customization.columns.moveTop')}
                  title={t('customization.columns.moveTop')}
                >
                  <ChevronsUp size={18} />
                </button>
                <button
                  type="button"
                  disabled={!canMoveUp}
                  onClick={() => moveSelected('up')}
                  aria-label={t('customization.columns.moveUp')}
                  title={t('customization.columns.moveUp')}
                >
                  <ChevronUp size={18} />
                </button>
                <button
                  type="button"
                  disabled={!canMoveDown}
                  onClick={() => moveSelected('down')}
                  aria-label={t('customization.columns.moveDown')}
                  title={t('customization.columns.moveDown')}
                >
                  <ChevronDown size={18} />
                </button>
                <button
                  type="button"
                  disabled={!canMoveDown}
                  onClick={() => moveSelected('bottom')}
                  aria-label={t('customization.columns.moveBottom')}
                  title={t('customization.columns.moveBottom')}
                >
                  <ChevronsDown size={18} />
                </button>
              </div>

              {/* Per-column settings — left pane */}
              <div className="lop-grid-customization__col-settings">
                {selectedMeta ? (
                  <ColumnSettingsPanel
                    meta={selectedMeta}
                    settings={selectedCustomization}
                    sampleValue={sampleValue}
                    onChange={(patch) => patchColumn(selectedMeta.field, patch)}
                  />
                ) : (
                  <div className="lop-grid-customization__col-empty">
                    {t('customization.columns.pickAColumn')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {errorMsg && (
          <div className="lop-grid-customization__error">{errorMsg}</div>
        )}

        <footer className="lop-grid-customization__footer">
          <LopButton
            variant="secondary"
            icon={<RotateCcw size={14} />}
            onClick={handleReset}
            disabled={saving}
          >
            {t('customization.resetDefaults')}
          </LopButton>
          <div className="lop-grid-customization__footer-right">
            <LopButton variant="secondary" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </LopButton>
            <LopButton variant="primary" onClick={handleSave} disabled={saving}>
              {t('common.save')}
            </LopButton>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------- Per-column settings panel ----------

interface ColumnSettingsPanelProps {
  meta: ColumnMeta;
  settings: ColumnCustomization;
  sampleValue: unknown;
  onChange: (patch: Partial<ColumnCustomization>) => void;
}

// Stable id for the format autocomplete datalist (one per panel mount).
let formatDatalistCounter = 0;

function ColumnSettingsPanel({
  meta,
  settings,
  sampleValue,
  onChange,
}: ColumnSettingsPanelProps) {
  const datalistId = useMemo(
    () => `lop-format-suggestions-${++formatDatalistCounter}`,
    [],
  );

  // Numeric = explicit cellDataType OR (inferred columns) a numeric sample
  // value OR a numeric-only setting already saved (e.g. via the right-click
  // "quick format" shortcut) — keeps the dialog consistent with the grid,
  // which colors/aggregates by value.
  const isNumeric =
    meta.isNumeric ||
    typeof sampleValue === 'number' ||
    Boolean(settings.signColoring) ||
    Boolean(settings.summary);

  const suggestions = FORMAT_OPTIONS.filter((opt) =>
    isNumeric ? opt.kind === 'number' : opt.kind === 'date',
  );

  const previewText =
    sampleValue === undefined || sampleValue === null || sampleValue === ''
      ? null
      : applyFormat(sampleValue, settings.format ?? null);
  const previewStyle = buildCellStyleForValue(settings, sampleValue, isNumeric);

  const align: 'start' | 'center' | 'end' = (settings.textAlign as any) ?? 'start';

  return (
    <div className="lop-grid-customization__panel">
      <div className="lop-grid-customization__panel-head">
        <h3>{meta.headerName}</h3>
        <span className="lop-grid-customization__panel-subtitle">
          {isNumeric
            ? t('customization.column.subtitleNumeric')
            : t('customization.column.subtitleText')}
        </span>
      </div>

      {/* Toolbar — bold/italic, alignment, colors, sign-coloring */}
      <div className="lop-grid-customization__toolbar" role="toolbar">
        <button
          type="button"
          className={`lop-grid-customization__tbtn lop-grid-customization__tbtn-bold ${
            settings.bold ? 'is-on' : ''
          }`}
          onClick={() => onChange({ bold: !settings.bold })}
          aria-pressed={Boolean(settings.bold)}
          title={t('customization.column.bold')}
        >
          B
        </button>
        <button
          type="button"
          className={`lop-grid-customization__tbtn lop-grid-customization__tbtn-italic ${
            settings.italic ? 'is-on' : ''
          }`}
          onClick={() => onChange({ italic: !settings.italic })}
          aria-pressed={Boolean(settings.italic)}
          title={t('customization.column.italic')}
        >
          I
        </button>

        <span className="lop-grid-customization__tbtn-sep" />

        <button
          type="button"
          className={`lop-grid-customization__tbtn ${align === 'start' ? 'is-on' : ''}`}
          onClick={() => onChange({ textAlign: 'start' })}
          aria-pressed={align === 'start'}
          title={t('customization.column.alignStart')}
        >
          <AlignRight size={14} />
        </button>
        <button
          type="button"
          className={`lop-grid-customization__tbtn ${align === 'center' ? 'is-on' : ''}`}
          onClick={() => onChange({ textAlign: 'center' })}
          aria-pressed={align === 'center'}
          title={t('customization.column.alignCenter')}
        >
          <AlignCenter size={14} />
        </button>
        <button
          type="button"
          className={`lop-grid-customization__tbtn ${align === 'end' ? 'is-on' : ''}`}
          onClick={() => onChange({ textAlign: 'end' })}
          aria-pressed={align === 'end'}
          title={t('customization.column.alignEnd')}
        >
          <AlignLeft size={14} />
        </button>

        <span className="lop-grid-customization__tbtn-sep" />

        <label
          className="lop-grid-customization__swatch lop-grid-customization__swatch-letter"
          title={t('customization.column.color')}
        >
          <span aria-hidden>A</span>
          <span
            className="lop-grid-customization__swatch-bar"
            style={{ background: settings.color ?? 'transparent' }}
          />
          <input
            type="color"
            value={settings.color ?? '#111827'}
            onChange={(e) => onChange({ color: e.target.value })}
          />
        </label>
        {settings.color && (
          <button
            type="button"
            className="lop-grid-customization__swatch-clear"
            onClick={() => onChange({ color: null })}
            aria-label={t('common.clear')}
            title={t('common.clear')}
          >
            <X size={12} />
          </button>
        )}

        <label
          className="lop-grid-customization__swatch lop-grid-customization__swatch-bg"
          title={t('customization.column.backgroundColor')}
        >
          <span
            className="lop-grid-customization__swatch-fill"
            style={{ background: settings.backgroundColor ?? 'transparent' }}
            aria-hidden
          />
          <input
            type="color"
            value={settings.backgroundColor ?? '#ffffff'}
            onChange={(e) => onChange({ backgroundColor: e.target.value })}
          />
        </label>
        {settings.backgroundColor && (
          <button
            type="button"
            className="lop-grid-customization__swatch-clear"
            onClick={() => onChange({ backgroundColor: null })}
            aria-label={t('common.clear')}
            title={t('common.clear')}
          >
            <X size={12} />
          </button>
        )}

        {isNumeric && (
          <>
            <span className="lop-grid-customization__tbtn-sep" />
            <button
              type="button"
              className={`lop-grid-customization__tbtn lop-grid-customization__sign-pn ${
                settings.signColoring ? 'is-on' : ''
              }`}
              onClick={() => onChange({ signColoring: !settings.signColoring })}
              aria-pressed={Boolean(settings.signColoring)}
              title={t('customization.column.signColoring')}
            >
              <span className="lop-grid-customization__sign-plus">+</span>
              <span className="lop-grid-customization__sign-minus">−</span>
            </button>
          </>
        )}
      </div>

      {/* Compact form rows */}
      <div className="lop-grid-customization__rows">
        <label className="lop-grid-customization__rrow">
          <span className="lop-grid-customization__rrow-label">
            {t('customization.column.font')}
          </span>
          <select
            value={settings.fontFamily ?? 'inherit'}
            onChange={(e) =>
              onChange({ fontFamily: e.target.value === 'inherit' ? null : e.target.value })
            }
          >
            {FONT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="lop-grid-customization__rrow">
          <span className="lop-grid-customization__rrow-label">
            {t('customization.column.width')}
          </span>
          <input
            type="number"
            min={20}
            step={10}
            value={settings.width ?? ''}
            placeholder={t('customization.column.widthAuto')}
            onChange={(e) => {
              const n = Number(e.target.value);
              onChange({ width: Number.isFinite(n) && n > 0 ? n : null });
            }}
          />
        </label>

        <label className="lop-grid-customization__rrow">
          <span className="lop-grid-customization__rrow-label">
            {t('customization.column.format')}
          </span>
          <input
            type="text"
            list={datalistId}
            value={settings.format ?? ''}
            onChange={(e) => onChange({ format: e.target.value || null })}
            placeholder={t('customization.column.formatPlaceholder')}
          />
          <datalist id={datalistId}>
            {suggestions.map((opt) => (
              <option key={opt.value} value={opt.value} label={opt.label} />
            ))}
          </datalist>
        </label>

        {isNumeric && (
          <label className="lop-grid-customization__rrow">
            <span className="lop-grid-customization__rrow-label">
              {t('customization.column.summary')}
            </span>
            <select
              value={settings.summary ?? ''}
              onChange={(e) =>
                onChange({
                  summary: (e.target.value === '' ? null : e.target.value) as ColumnCustomization['summary'],
                })
              }
            >
              <option value="">{t('customization.column.summaryNone')}</option>
              {SUMMARY_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Live preview */}
      <div className="lop-grid-customization__preview">
        <label>{t('customization.column.preview')}</label>
        {previewText !== null ? (
          <div className="lop-grid-customization__preview-cell" style={previewStyle}>
            {previewText}
          </div>
        ) : (
          <div className="lop-grid-customization__preview-empty">
            {t('customization.column.previewNoSample')}
          </div>
        )}
      </div>
    </div>
  );
}
